import React from 'react';
import { ArrowRight } from 'lucide-react';
import styles from './Hero.module.css';
import { FloatingSphere, IsoCube, HexPrism } from '../../components/Svg3D/Svg3D';
import heroAi from '../../assets/hero-ai.png';
import heroLlm from '../../assets/hero-llm.png';
import heroMultiagent from '../../assets/hero-multiagent.png';
import heroCode from '../../assets/hero-code.png';
import heroDeploy from '../../assets/hero-deploy.png';
import heroAnalytics from '../../assets/hero-analytics.png';

const Hero = ({ onNavigate }) => {
  return (
    <section id="hero" className={styles.hero}>
      <div className={styles.container}>
        {/* Top — text content */}
        <div className={styles.textBlock}>
          <span className={styles.tag}>
            Business Transformation | AI Engineering | Cloud Services
          </span>

          <h1 className={styles.title}>
            Your AI Initiatives Deserve Better Than POC Purgatory
          </h1>

          <p className={styles.subtitle}>
            BlueprintX is the execution partner that takes your AI, cloud, and
            digital initiatives from strategy to production — in weeks, not
            quarters.
          </p>

          <div className={styles.ctaRow}>
            <button
              className={styles.primaryCta}
              onClick={() => onNavigate('contact')}
            >
              Start a Conversation
              <ArrowRight size={18} />
            </button>
            <button
              className="bracket-btn bracket-btn--light"
              onClick={() => onNavigate('services')}
            >
              See Our Services
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Horizontal panels row */}
      <div className={styles.panelsRow}>
        <div className={styles.panel}>
          <img src={heroAi} alt="AI & Neural Networks" className={styles.panelImg} />
          <span className={styles.panelLabel}>AI & Neural Networks</span>
        </div>
        <div className={styles.panel}>
          <img src={heroAnalytics} alt="LLM Solutions" className={styles.panelImg} />
          <span className={styles.panelLabel}>LLM Solutions</span>
        </div>
        <div className={styles.panel}>
          <img src={heroDeploy} alt="Multi-Agent Systems" className={styles.panelImg} />
          <span className={styles.panelLabel}>Multi-Agent Systems</span>
        </div>
        <div className={styles.panel}>
          <img src={heroLlm} alt="Software Development" className={styles.panelImg} />
          <span className={styles.panelLabel}>Software Development</span>
        </div>
        <div className={styles.panel}>
          <img src={heroCode} alt="Cloud Deployment" className={styles.panelImg} />
          <span className={styles.panelLabel}>Cloud Deployment</span>
        </div>
        <div className={styles.panel}>
          <img src={heroMultiagent} alt="Analytics & Insights" className={styles.panelImg} />
          <span className={styles.panelLabel}>Analytics & Insights</span>
        </div>
      </div>

      {/* Stats row */}
      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statValue}>Amsterdam + Chennai</span>
          <span className={styles.statLabel}>Dual-Hub Delivery</span>
        </div>
        <div className={styles.statDivider} />
        <div className={styles.stat}>
          <span className={styles.statValue}>14 Service Domains</span>
          <span className={styles.statLabel}>Full-Stack Capability</span>
        </div>
        <div className={styles.statDivider} />
        <div className={styles.stat}>
          <span className={styles.statValue}>Weeks, Not Quarters</span>
          <span className={styles.statLabel}>Compressed Delivery</span>
        </div>
      </div>

      {/* Floating decorative elements — Visify-style collage accents */}
      <FloatingSphere position={{ top: '15%', right: '22%' }} size={160} opacity={0.5} />
      <IsoCube position={{ bottom: '12%', left: '12%' }} size={120} opacity={0.45} />
      <HexPrism position={{ top: '55%', right: '12%' }} size={110} opacity={0.4} />
      <div className={styles.floatingElements} aria-hidden="true">
        {/* Squiggly underline accent — top left */}
        <svg className={`${styles.floatEl} ${styles.squiggle1}`} viewBox="0 0 120 20" fill="none">
          <path d="M2,10 Q15,2 28,10 Q41,18 54,10 Q67,2 80,10 Q93,18 106,10 Q112,6 118,10"
            stroke="#a2de21" strokeWidth="2.5" strokeLinecap="round" fill="none" />
        </svg>

        {/* Crosshair / target — top right area */}
        <svg className={`${styles.floatEl} ${styles.crosshair1}`} viewBox="0 0 40 40" fill="none">
          <circle cx="20" cy="20" r="12" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" fill="none" />
          <circle cx="20" cy="20" r="4" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" fill="none" />
          <line x1="20" y1="2" x2="20" y2="14" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
          <line x1="20" y1="26" x2="20" y2="38" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
          <line x1="2" y1="20" x2="14" y2="20" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
          <line x1="26" y1="20" x2="38" y2="20" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
        </svg>

        {/* Brain outline — left side */}
        <svg className={`${styles.floatEl} ${styles.brain}`} viewBox="0 0 80 90" fill="none">
          <path d="M40,8 C25,8 15,18 15,30 C10,32 8,40 10,48 C8,52 8,60 14,65 C14,72 20,80 30,82 C35,85 45,85 50,82 C60,80 66,72 66,65 C72,60 72,52 70,48 C72,40 70,32 65,30 C65,18 55,8 40,8Z"
            stroke="rgba(255,255,255,0.2)" strokeWidth="2" fill="none" />
          <path d="M40,20 C40,40 40,70 40,82" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
          <path d="M25,35 Q32,30 40,35 Q48,30 55,35" stroke="rgba(255,255,255,0.12)" strokeWidth="1" fill="none" />
          <path d="M20,50 Q30,45 40,50 Q50,45 60,50" stroke="rgba(255,255,255,0.12)" strokeWidth="1" fill="none" />
          {/* Lightbulb inside brain */}
          <circle cx="40" cy="38" r="8" stroke="#a2de21" strokeWidth="2" fill="none" opacity="0.6" />
          <path d="M36,46 L44,46 M37,49 L43,49" stroke="#a2de21" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
          <path d="M38,30 L40,34 L42,30" stroke="#a2de21" strokeWidth="1.5" fill="none" opacity="0.7" />
        </svg>

        {/* Hand-drawn arrow — curved swoosh */}
        <svg className={`${styles.floatEl} ${styles.swooshArrow}`} viewBox="0 0 100 60" fill="none">
          <path d="M5,50 Q25,5 60,15 Q80,20 90,10"
            stroke="#a2de21" strokeWidth="2.5" strokeLinecap="round" fill="none" />
          <path d="M82,8 L92,10 L86,18" stroke="#a2de21" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>

        {/* Scatter X marks */}
        <svg className={`${styles.floatEl} ${styles.xMark1}`} viewBox="0 0 20 20" fill="none">
          <line x1="4" y1="4" x2="16" y2="16" stroke="#a2de21" strokeWidth="2" strokeLinecap="round" />
          <line x1="16" y1="4" x2="4" y2="16" stroke="#a2de21" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <svg className={`${styles.floatEl} ${styles.xMark2}`} viewBox="0 0 20 20" fill="none">
          <line x1="4" y1="4" x2="16" y2="16" stroke="rgba(255,255,255,0.15)" strokeWidth="2" strokeLinecap="round" />
          <line x1="16" y1="4" x2="4" y2="16" stroke="rgba(255,255,255,0.15)" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <svg className={`${styles.floatEl} ${styles.xMark3}`} viewBox="0 0 20 20" fill="none">
          <line x1="4" y1="4" x2="16" y2="16" stroke="#a2de21" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
          <line x1="16" y1="4" x2="4" y2="16" stroke="#a2de21" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
        </svg>

        {/* Circuit trace path — decorative */}
        <svg className={`${styles.floatEl} ${styles.circuit1}`} viewBox="0 0 120 40" fill="none">
          <path d="M0,20 L20,20 L25,8 L50,8 L55,20 L75,20 L80,32 L100,32 L105,20 L120,20"
            stroke="rgba(255,255,255,0.18)" strokeWidth="2" fill="none" />
          <circle cx="25" cy="8" r="4" fill="#a2de21" opacity="0.5" />
          <circle cx="80" cy="32" r="4" fill="#a2de21" opacity="0.4" />
        </svg>

        {/* Dotted circle / orbit */}
        <svg className={`${styles.floatEl} ${styles.orbit}`} viewBox="0 0 60 60" fill="none">
          <circle cx="30" cy="30" r="25" stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="4 6" fill="none" className={styles.spinSlow} />
          <circle cx="30" cy="5" r="4" fill="#a2de21" opacity="0.5" className={styles.spinSlow} />
        </svg>

        {/* Small line chart squiggle — right side */}
        <svg className={`${styles.floatEl} ${styles.miniChart}`} viewBox="0 0 80 40" fill="none">
          <polyline points="5,30 15,25 25,28 35,15 45,18 55,8 65,12 75,5"
            stroke="rgba(255,255,255,0.25)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="55" cy="8" r="4" fill="#a2de21" opacity="0.5" />
          <circle cx="75" cy="5" r="3" fill="#a2de21" opacity="0.7" />
        </svg>

        {/* Code brackets — floating */}
        <svg className={`${styles.floatEl} ${styles.brackets}`} viewBox="0 0 50 40" fill="none">
          <path d="M8,5 L2,20 L8,35" stroke="rgba(255,255,255,0.3)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
          <path d="M42,5 L48,20 L42,35" stroke="rgba(255,255,255,0.3)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
          <text x="25" y="24" fill="#a2de21" fontSize="12" textAnchor="middle" fontFamily="monospace" opacity="0.6">/</text>
        </svg>

        {/* Scatter dots */}
        <svg className={`${styles.floatEl} ${styles.dots1}`} viewBox="0 0 30 30" fill="none">
          <circle cx="5" cy="5" r="3.5" fill="rgba(255,255,255,0.2)" />
          <circle cx="18" cy="18" r="2.5" fill="rgba(255,255,255,0.15)" />
          <circle cx="26" cy="6" r="4" fill="#a2de21" opacity="0.35" />
        </svg>
        <svg className={`${styles.floatEl} ${styles.dots2}`} viewBox="0 0 30 30" fill="none">
          <circle cx="8" cy="22" r="3" fill="#a2de21" opacity="0.35" />
          <circle cx="22" cy="8" r="3.5" fill="rgba(255,255,255,0.18)" />
          <circle cx="16" cy="26" r="2" fill="rgba(255,255,255,0.15)" />
        </svg>
      </div>

      {/* Trust strip */}
      <div className={styles.trustStrip}>
        <span className={styles.trustLabel}>Trusted by forward-thinking enterprises</span>
        <div className={styles.trustLogos}>
          <span className={styles.trustLogo}>FORTUNE 500</span>
          <span className={styles.trustLogo}>SERIES B+</span>
          <span className={styles.trustLogo}>ENTERPRISE</span>
          <span className={styles.trustLogo}>GOV & PUBLIC</span>
        </div>
      </div>
    </section>
  );
};

export default Hero;
